/**
 * Persona-aware post-auth destination resolver — the single source of truth
 * for "where does a freshly authenticated user land" (spec §5 Track-0,
 * persona-auth split D6a). Shared by the password login screens
 * (AuthLoginScreen.onSubmit) and the OAuth handoff (OAuthHandoffScreen).
 *
 * Rule: a safe same-origin ?return_to wins; otherwise the persona's door
 * default applies (/admin | /sellers/me | /chat). When a user lands at a
 * persona door they don't belong to AND no return_to is present, the resolver
 * reports `wrongDoor` so the screen shows an inline message instead of
 * bouncing them into a guard they cannot pass.
 *
 * The consumer door is the one asymmetric case: an admin account does NOT
 * belong there (an admin is a distinct persona, not "a user" — the admin
 * portal is the only door for that identity), so it reports `wrongDoor` with
 * a CTA to /admin/login. A seller account still matches the consumer door
 * (only nudged toward /seller/login, never rejected) — sellers remain
 * regular users of the consumer app.
 *
 * Consolidates the same-origin `safeReturnTo` heuristic that previously lived
 * (duplicated) in LoginScreen, OAuthHandoffScreen, and RedirectIfAuthenticated.
 */
import { isAdminPermission } from "@/features/admin/navConfig";

export type AuthPersona = "consumer" | "seller" | "admin";

/** The slice of /me the resolver needs (a subset of MeProfile). */
export interface PersonaIdentity {
  permissions: string[];
  is_seller: boolean;
}

export interface AuthDestination {
  /** Where to navigate, or null when `wrongDoor` is true (no navigation). */
  path: string | null;
  /**
   * True when the user authenticated at a door they don't belong to AND no
   * safe return_to was present. Drives the inline wrong-door CTA.
   */
  wrongDoor: boolean;
}

/** Per-persona door default when no safe return_to is present. */
export const DOOR_DEFAULT: Record<AuthPersona, string> = {
  // The consumer home is the assistant (chat). The captured-renovation
  // "project" dashboard was hidden product-wide (2026-07-06).
  consumer: "/chat",
  seller: "/sellers/me",
  admin: "/admin",
};

/** Mirrors the server's _SAFE_RETURN_TO_RE (apps/api/garden/auth/oauth/router_google.py:108). */
const SAFE_RETURN_TO_RE = /^\/[^/\\]/;

/**
 * A same-origin relative path, or null. Must start with a single "/" followed
 * by a character that is neither "/" nor "\" — rejecting empty strings,
 * protocol-relative ("//evil"), absolute ("https://…"), and backslash
 * open-redirect ("/\\evil", "/\\/evil") inputs. Mirrors the server's
 * `_is_safe_return_to` via the shared `SAFE_RETURN_TO_RE` regex above.
 */
export function safeReturnToOrNull(raw: string | null): string | null {
  if (raw === null) return null;
  if (!SAFE_RETURN_TO_RE.test(raw)) return null;
  return raw;
}

/**
 * `path` with the incoming `return_to` carried through — the ONE way an auth
 * screen links to another auth screen (W0.1, SCRUM-276). The value goes
 * through `safeReturnToOrNull`, so a bare `/`, a protocol-relative or a
 * backslash path is dropped rather than forwarded; a query `path` already
 * carries (`/signup?email=…`) is kept.
 */
export function withReturnTo(path: string, rawReturnTo: string | null): string {
  const safe = safeReturnToOrNull(rawReturnTo);
  if (safe === null) return path;
  const q = path.indexOf("?");
  const base = q === -1 ? path : path.slice(0, q);
  const params = new URLSearchParams(q === -1 ? "" : path.slice(q + 1));
  params.set("return_to", safe);
  return base + "?" + params.toString();
}

/** True iff this identity belongs to the given persona's door. */
export function matchesPersona(
  persona: AuthPersona,
  identity: PersonaIdentity,
): boolean {
  switch (persona) {
    case "consumer":
      // Every identity is a consumer EXCEPT an admin — admin is a distinct
      // persona with its own door, not a superset of "user".
      return !identity.permissions.some(isAdminPermission);
    case "seller":
      return identity.is_seller;
    case "admin":
      return identity.permissions.some(isAdminPermission);
  }
}

/**
 * The persona login door that owns an in-app area path — `/admin/login` for
 * anything under `/admin`, `/seller/login` for `/seller*` and `/sellers*`,
 * else the consumer `/login`. (Track 0 persona-auth Task 6, "logout by door".)
 *
 * SCRUM-145: sending an admin or a seller to the consumer door is the whole
 * bug — the personas have separate front doors and a guard must hand a visitor
 * to the one that owns the path they asked for.
 *
 * Matching is by path SEGMENT, never bare prefix: `/administration` is not the
 * admin area.
 */
export function loginDoorForPath(pathname: string): string {
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return "/admin/login";
  // Covers both the singular door (/seller/login) and the plural area
  // (/sellers/me, /sellers/register).
  if (
    pathname === "/seller" ||
    pathname === "/sellers" ||
    pathname.startsWith("/seller/") ||
    pathname.startsWith("/sellers/")
  ) {
    return "/seller/login";
  }
  return "/login";
}

export function resolveAuthDestination(
  persona: AuthPersona,
  rawReturnTo: string | null,
  identity: PersonaIdentity,
): AuthDestination {
  const safe = safeReturnToOrNull(rawReturnTo);
  if (safe !== null) {
    return { path: safe, wrongDoor: false };
  }
  if (matchesPersona(persona, identity)) {
    return { path: DOOR_DEFAULT[persona], wrongDoor: false };
  }
  return { path: null, wrongDoor: true };
}
